import React, { useEffect, useState } from 'react';

import { templateDetailParams } from 'api';
import { useApp } from 'contexts';
import { useQuery } from 'hooks';

type TemplateContext = {
	template: any;
	setTemplate: any;
	options: any;
	setOptions: any;
	isOwner: boolean;
	rootApi: string;
	rootLink: string;
	loading: boolean;
	onUpdate: any;
	onRefresh: any;
};

export const TemplateContext = React.createContext<TemplateContext>(null);

const TemplateProvider = ({ ssr, workspace, router, session, store, toast, children }) => {
	const [template, setTemplate] = useState(ssr.template?.data || {});
	const [options, setOptions] = useState({
		collapsed: false,
		sidebar: true,
		browser: {
			actions: true,
			footer: true,
		},
	});
	const app = useApp();

	// Computed
	const rootApi = template.uuid ? `/templates/${template.uuid}` : '';
	const rootLink = template.uuid ? `/explore/projects/${template.uuid}` : '';
	const isOwner = workspace.workspace?.id === template.workspaceId;

	// Hooks
	useEffect(() => {
		setTemplate(ssr.template?.data || {});
		return () => onClear();
	}, [ssr.template, router.asPath]);

	useEffect(() => {
		setOptions({ ...options, sidebar: app.size.md });
	}, [app.size.md]);

	useEffect(() => {
		if (template.id !== ssr.template?.data.id) setOptions({ ...options, collapsed: false });
	}, [template.id]);

	// Actions
	const [fetchData, loading] = useQuery(async () => {
		if (!rootApi) return;
		const response = await store.queryRecord({ url: rootApi, params: templateDetailParams });
		setTemplate(response.data);
	});

	// Methods
	const onUpdate = data => setTemplate({ ...template, ...data });
	const onRefresh = () => fetchData();
	const onClear = () => setTemplate({});

	// Render
	return (
		<TemplateContext.Provider
			value={{
				template,
				setTemplate,
				options,
				setOptions,
				isOwner,
				rootApi,
				rootLink,
				loading,
				onUpdate,
				onRefresh,
			}}>
			{children}
		</TemplateContext.Provider>
	);
};

const useTemplate = () => {
	return React.useContext(TemplateContext);
};

export { TemplateProvider, useTemplate };
